import React, { useEffect, useMemo } from "react";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import {
    buildCct,
    buildContract,
    buildCreateMission,
    buildMissionDeduction,
    buildMissionFee,
    buildSalaryForecast,
    EARNING_TYPE_SAMPLES,
} from "../../factories";
import RootCreateMissionSummary from "../../../screens/create-mission/CreateMissionSummary";
import { currentLang } from "../../../atoms/i18n";
import { newMissionState } from "../../../atoms/core";
import { useSetAtom } from "jotai";
import { MockedProvider } from "@apollo/client/testing/react";
import { GET_SALARY_FORECAST } from "../../../graphql/salaries";
import { EarningType, FeeType, ProfileType } from "../../../__generated__/graphql";
import { buildClientInfo, buildUser } from "../../factories";
import type { StoryObj } from "@storybook/react";
import { GET_CCT_BY_EDITION_BY } from "../../../graphql/cct";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import { AtomsHydrator } from "../../../atoms/atoms-hydrator";
import { MockLink } from "@apollo/client/testing";

const StoryComponent = ({
    hoursPerWeekFullTime,
    earningType,
}: {
    hoursPerWeekFullTime: number;
    earningType: EarningType;
}) => {
    const setNewMissionState = useSetAtom(newMissionState);

    const mission = useMemo(
        () =>
            buildCreateMission({
                currentContract: buildContract({
                    netSalary: 50000,
                    invoiceSalary: 60000,
                    grossSalary: 55000,
                    hoursPerWeekFullTime: hoursPerWeekFullTime,
                    earningType,
                    workingTimeClassicSpec: EARNING_TYPE_SAMPLES[earningType],
                    fees: [
                        buildMissionFee(),
                        buildMissionFee({ type: FeeType.Other, otherTitle: "Orangina 1L" }),
                    ],
                    deductions: [buildMissionDeduction()],
                }),
                ijmSelection: {
                    ijmGroupName: "Le plan de folie",
                    ijmGroupId: "ijm-group-1",
                    ijmPayFirstDays: true,
                    ijmPayRemainingPercents: 10,
                },
            }),
        [hoursPerWeekFullTime, earningType],
    );

    useEffect(() => {
        setNewMissionState(mission);
    }, [setNewMissionState, mission]);

    const user = useMemo(
        () =>
            buildUser({
                clientInfos: [buildClientInfo({ id: "client" })],
            }),
        [],
    );

    const mocks = useMemo<MockLink.MockedResponse[]>(
        () => [
            {
                request: {
                    query: GET_SALARY_FORECAST,
                    variables: () => true,
                },
                result: () => ({
                    data: { getSalaryForecast: [buildSalaryForecast()] },
                }),
            },
            {
                request: {
                    query: GET_CCT_BY_EDITION_BY,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        getCctByEditionId: buildCct({
                            cctEditionId: "cct-3-A",
                            name: "LA seule qui marche",
                            cctContractId: "815001", // see env.CCT_DEFAULT_CONTRACT_ID
                            hoursPerWeek: 44.0,
                            intercomMainArticleId: "8953615",
                            intercomHelpArticleId: "8953621",
                        }),
                    },
                }),
            },
        ],
        [],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Client}>
                <AtomsHydrator
                    key={earningType} // important to re-create on change
                    atomValues={[
                        [currentLang, "fr"],
                        [newMissionState, mission],
                    ]}
                >
                    <MockedProvider mocks={mocks}>
                        <RootCreateMissionSummary />
                    </MockedProvider>
                </AtomsHydrator>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "CreateMission/Summary",
    component: StoryComponent,
    args: {
        hoursPerWeekFullTime: 42,
        earningType: EarningType.Monthly,
    },
    argTypes: {
        hoursPerWeekFullTime: {
            options: [30, 35, 40, 42, 45],
            control: { type: "select" },
        },
        earningType: {
            options: [
                EarningType.Monthly,
                EarningType.Daily,
                EarningType.Hourly,
                EarningType.Package,
            ],
            control: { type: "select" },
        },
    },
};

export default meta;

type Story = StoryObj<typeof meta>;

export const Classic: Story = {};
